<script lang="ts">
    import type { OcCinemaV1Events } from '@otto-ec/otto-components/cinema';
    import { afterPageLoad } from '@tilelist/components/black-friday-slider/afterPageLoad.browser.ts';
    import { allTileIndices, visibleTiles } from '@tilelist/components/black-friday-slider/visibleTiles.browser.ts';
    import ProductTileItem from '@tilelist/components/product-tile-item/ProductTileItem.svelte';
    import type { BlackFridaySliderItem, ProductItem } from '@tilelist/domain/composition/TileListitem.ts';
    import type { TileListPageContext } from '@tilelist/domain/composition/TileListPage.ts';

    type Props = {
        slider: BlackFridaySliderItem;
        context: TileListPageContext;
        cacheRestored?: boolean;
        showRadioEquipmentRegulation?: boolean;
        headline?: string;
        subline?: string;
        onLoad: () => void;
        /*                                                                                    */
        onTilesShown: (tiles: ProductItem[]) => void;
        onProductClick: (event: Event, item: ProductItem) => void;
    };

    let {
        slider,
        context,
        cacheRestored = true,
        showRadioEquipmentRegulation = false,
        headline = 'Black Friday Deals',
        subline = 'Die top bewerteten Produkte - bis 1.12 täglich wechselnd',
        onLoad,
        onTilesShown,
        onProductClick,
    }: Props = $props();

    const VISIBLE_RATIO = 0.5;
    const BROWSE_SOURCES: ReadonlySet<string> = new Set(['arrows', 'swipe', 'pagination-dots']);

    let root = $state<HTMLElement | null>(null);
    let loadRequested = false;
    let visibleReported = false;
    let inViewport = false;
    /*                                                                    */
    let shownIndices: number[] | undefined;
    let visibilityRetryScheduled = false;

    /*                                                                                                            */
    $effect(() => {
        if (slider.state !== 'pending') {
            /*                                                                               */
            loadRequested = false;
            return;
        }
        if (!root || !cacheRestored || loadRequested) return;
        visibleReported = false;
        inViewport = false;
        shownIndices = undefined;
        visibilityRetryScheduled = false;
        return afterPageLoad(() => {
            if (loadRequested) return;
            loadRequested = true;
            onLoad();
        });
    });

    /*                                                                                     */
    $effect(() => {
        if (!root || slider.state !== 'ready' || visibleReported) return;
        const observer = new IntersectionObserver(
            (entries) => {
                inViewport = entries.some((entry) => entry.intersectionRatio >= VISIBLE_RATIO);
                reportVisible();
                if (visibleReported) observer.disconnect();
            },
            { threshold: VISIBLE_RATIO },
        );
        observer.observe(root);
        return () => observer.disconnect();
    });

    function sliderItems(): ProductItem[] {
        return slider.state === 'ready' ? slider.items : [];
    }

    function reportVisible() {
        if (visibleReported || !inViewport) return;
        const items = sliderItems();
        const tiles = visibleTiles(root, items, shownIndices ?? allTileIndices(items));
        if (!tiles) {
            retryVisibleReport();
            return;
        }
        visibleReported = true;
        onTilesShown(tiles);
    }

    function retryVisibleReport() {
        if (visibilityRetryScheduled) return;
        visibilityRetryScheduled = true;
        requestAnimationFrame(() => {
            visibilityRetryScheduled = false;
            reportVisible();
        });
    }

    function reportBrowse(indices: number[]) {
        const tiles = visibleTiles(root, sliderItems(), indices);
        if (!tiles) {
            requestAnimationFrame(() => reportBrowse(indices));
            return;
        }
        onTilesShown(tiles);
    }

    function onVisibilityChanged({ detail }: OcCinemaV1Events['oc-item-visibility-changed']) {
        shownIndices = detail.visibleItems.indices;
        reportVisible();
    }

    function onScrollCompleted({ detail }: OcCinemaV1Events['oc-stage-scroll-completed']) {
        shownIndices = detail.visibleItems.indices;
        if (!detail.source || !BROWSE_SOURCES.has(detail.source)) return;
        reportBrowse(detail.visibleItems.indices);
    }
</script>

{#if slider.state !== 'collapsed'}
    <section
        bind:this={root}
        class="reptile-black-friday-slider"
        class:reptile-black-friday-slider--pending={slider.state === 'pending'}
        class:reptile-black-friday-slider--ready={slider.state === 'ready'}
        data-testid="black-friday-slider"
        aria-label={headline}
    >
        {#if slider.state === 'ready'}
            <header class="reptile-black-friday-slider__header">
                <h2 class="reptile-black-friday-slider__headline oc-headline-200">{headline}</h2>
                <p class="reptile-black-friday-slider__subline oc-copy-100">{subline}</p>
            </header>
            <div class="reptile-black-friday-slider__body">
                <oc-cinema-v1
                    oc-aria-label={headline}
                    resizing="none"
                    gap="8px"
                    stage-spacing="0px"
                    onoc-item-visibility-changed={onVisibilityChanged}
                    onoc-stage-scroll-completed={onScrollCompleted}
                >
                    {#each slider.items as item (item.product.id)}
                        <div class="reptile-black-friday-slider__tile">
                            <ProductTileItem
                                {cacheRestored}
                                productItem={item}
                                rule={context.rule}
                                layout={context.layout}
                                loadVariation={async () => {}}
                                onSeen={async () => {}}
                                onTileClick={(event) => onProductClick(event, item)}
                                {showRadioEquipmentRegulation}
                                showTopInfos={false}
                                showInstallments={false}
                                showAvailability={false}
                                showComparison={false}
                                showMultipleSizes={false}
                                searchContext={context.searchContext}
                            />
                        </div>
                    {/each}
                </oc-cinema-v1>
            </div>
        {/if}
    </section>
{/if}

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';
    @use '@otto-ec/otto-components-utils/scss/mixins';

    $tile-width: 8.75rem;
    $pending-min-height: 30rem;

    .reptile-black-friday-slider {
        grid-column: 1 / -1;
        margin-inline: calc(-1 * #{tokens.$oc-semantic-spacing-absolute-25});

        @include mixins.breakpoint-from-l {
            margin-inline: 0;
        }

        /*                                                                             */
        &--ready {
            background-color: tokens.$oc-base-color-black-100;
            background-image: linear-gradient(
                to top,
                rgb(255 255 255 / 20%) calc(100% / 3),
                transparent calc(100% / 3)
            );
        }

        /*                                                            */
        &--pending {
            min-height: $pending-min-height;
        }

        &__header {
            padding: tokens.$oc-semantic-spacing-absolute-100 tokens.$oc-semantic-spacing-absolute-100
                tokens.$oc-semantic-spacing-absolute-50;
            color: tokens.$oc-semantic-color-text-inverted;
        }

        &__headline,
        &__subline {
            margin: 0;
        }

        &__body {
            padding: tokens.$oc-semantic-spacing-absolute-25 tokens.$oc-semantic-spacing-absolute-50
                tokens.$oc-semantic-spacing-absolute-50;

            @include mixins.breakpoint-from-l {
                padding-inline: tokens.$oc-semantic-spacing-absolute-50;
            }
        }

        &__tile {
            display: grid;
            width: $tile-width;

            &:first-child {
                margin-inline-start: tokens.$oc-semantic-spacing-absolute-50;
            }

            &:last-child {
                margin-inline-end: tokens.$oc-semantic-spacing-absolute-50;
            }

            @include mixins.breakpoint-from-l {
                &:first-child,
                &:last-child {
                    margin-inline: 0;
                }
            }
        }
    }
</style>
